import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  TrendingUp, Menu, X, LogOut, Settings as SettingsIcon, Bell,
  ChevronDown, Lightbulb, Activity, AlertTriangle, BarChart3,
  Wallet, BookOpen, Building2, Target, Scale, Clock, Shield, CalendarRange, Sparkles,
  MessageSquare, Twitter, Youtube, Instagram, MessageCircle, Mail, Music
} from 'lucide-react';
import axios from 'axios';
import { API } from '../config/api';
import { AuthContext } from '../App';
import ThemeToggle from './ThemeToggle';
import { getEffectiveTier, TIERS } from '../hooks/useSubscription';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Admin email
const ADMIN_EMAIL = 'cepedafa662@gmail.com';

export default function Navigation({ user: propUser, onLogout: propOnLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const dropdownRef = useRef(null);
  
  // Try to get auth context, fallback to props
  const authContext = useContext(AuthContext);
  const user = propUser || authContext?.user;
  const contextLogout = authContext?.logout;
  
  // Check if user is admin
  const isAdmin = user?.email === ADMIN_EMAIL;

  // Fetch unread alerts count — derived from the alerts array (single source of truth).
  // We intentionally ignore response.data.unread_count so header, Alerts Center page
  // header, and footer all report the same number.
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/alerts`, { withCredentials: true });
        const list = response.data?.alerts || [];
        setUnreadAlerts(list.filter(a => !a.is_read).length);
      } catch (error) {
        // Silently fail
      }
    };
    fetchAlerts();
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setToolsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear auth state using context or prop callback
    if (contextLogout) {
      contextLogout();
    } else if (propOnLogout) {
      propOnLogout();
    }
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path) => 
    `px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
      isActive(path) 
        ? 'text-brand-blue border-brand-blue font-semibold' 
        : 'text-gray-600 dark:text-gray-300 border-transparent hover:text-brand-blue dark:hover:text-brand-blue'
    }`;

  return (
    <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center space-x-6">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="nav-brand-logo-link">
              <img
                src="/logo-nav.png"
                alt=""
                className="h-10 w-auto"
                data-testid="nav-brand-logo"
              />
              <span className="text-2xl font-dm-sans font-bold tracking-tight leading-none">
                <span style={{ color: '#1a2b5b' }}>Portfolio</span><span style={{ color: '#00aaff' }}>Engine</span>
              </span>
            </Link>
            
            {/* Main Nav Links */}
            <div className="hidden lg:flex items-center space-x-1">
              <Link to="/dashboard" className={navLinkClass('/dashboard')} data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/market-analysis" className={navLinkClass('/market-analysis')} data-testid="nav-market">
                Market
              </Link>
              <Link to="/portfolio/list" className={navLinkClass('/portfolio/list')} data-testid="nav-portfolios">
                Portfolios
              </Link>
              
              {/* V2 Tools Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setToolsDropdownOpen(!toolsDropdownOpen)}
                  className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                    ['/guidance', '/portfolio/rebalancing', '/backtesting', '/simulation', '/alerts', '/activity', '/instruments/evaluate', '/instruments/compare'].includes(location.pathname)
                      ? 'text-forest-900 font-semibold'
                      : 'text-gray-600 hover:text-forest-900'
                  }`}
                  data-testid="nav-tools"
                >
                  Tools
                  <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${toolsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {toolsDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-2 z-50">
                    <Link 
                      to="/guidance" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Lightbulb className="h-4 w-4 mr-3 text-yellow-500" />
                      Action Guidance
                    </Link>
                    <Link 
                      to="/portfolio/rebalancing" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Scale className="h-4 w-4 mr-3 text-teal-500" />
                      Portfolio Rebalancing
                    </Link>
                    <Link 
                      to="/backtesting" 
                      className="flex items-start px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Activity className="h-4 w-4 mr-3 text-blue-500 mt-0.5" />
                      <div>
                        <div className="font-medium">Strategy Backtesting</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Compare 6 rebalancing strategies on your actual holdings</div>
                      </div>
                    </Link>
                    <Link 
                      to="/simulation" 
                      className="flex items-start px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                      data-testid="nav-simulation"
                    >
                      <CalendarRange className="h-4 w-4 mr-3 text-amber-500 mt-0.5" />
                      <div>
                        <div className="font-medium">Historical Simulation</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Model any time period using your asset allocation</div>
                      </div>
                    </Link>
                    <Link 
                      to="/alerts" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Bell className="h-4 w-4 mr-3 text-red-500" />
                      Alerts Center
                      {unreadAlerts > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {unreadAlerts}
                        </span>
                      )}
                    </Link>
                    <Link 
                      to="/activity" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Clock className="h-4 w-4 mr-3 text-indigo-500" />
                      Activity History
                    </Link>
                    <Link 
                      to="/ai-portfolio-builder" 
                      className="flex items-start px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-forest-50 dark:hover:bg-gray-700"
                      onClick={() => setToolsDropdownOpen(false)}
                      data-testid="nav-ai-portfolio-builder"
                    >
                      <Sparkles className="h-4 w-4 mr-3 text-emerald-500 mt-0.5" />
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          AI Portfolio Builder
                          <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">PREMIUM</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Generate a portfolio from a natural language description</div>
                      </div>
                    </Link>
                    <div className="border-t border-gray-100 my-2"></div>
                    <Link 
                      to="/instruments/evaluate" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-forest-50"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <Target className="h-4 w-4 mr-3 text-green-500" />
                      Instrument Evaluator
                    </Link>
                    <Link 
                      to="/instruments/compare" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-forest-50"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <BarChart3 className="h-4 w-4 mr-3 text-purple-500" />
                      Compare Instruments
                    </Link>
                  </div>
                )}
              </div>
              
              <Link to="/education" className={navLinkClass('/education')} data-testid="nav-education">
                Learn
              </Link>
              <Link to="/brokers" className={navLinkClass('/brokers')} data-testid="nav-brokers">
                Brokers
              </Link>
            </div>
          </div>
          
          {/* Right Side */}
          <div className="flex items-center space-x-3">
            {/* Tier Badge */}
            {(() => {
              const tier = getEffectiveTier(user);
              if (tier === TIERS.ADMIN) return (
                <span data-testid="tier-badge-admin" className="hidden sm:block px-2.5 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full shadow-sm">ADMIN</span>
              );
              if ([TIERS.PREMIUM, TIERS.PREMIUM_MONTHLY, TIERS.PREMIUM_LIFETIME].includes(tier)) return (
                <span data-testid="tier-badge-premium" className="hidden sm:block px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs font-bold rounded-full shadow-sm">PREMIUM</span>
              );
              if (tier === TIERS.BOOK_BUNDLE) return (
                <span data-testid="tier-badge-book-bundle" className="hidden sm:block px-2.5 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold rounded-full shadow-sm">BOOK BUNDLE</span>
              );
              return null;
            })()}
            
            {/* Alerts Bell */}
            <button 
              onClick={() => navigate('/alerts')} 
              className="relative p-2 text-gray-600 hover:text-forest-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Alerts"
            >
              <Bell className="h-5 w-5" />
              {unreadAlerts > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </button>
            
            {/* User Name (Desktop) */}
            <span className="text-sm text-gray-600 dark:text-gray-300 hidden md:block">{user?.name?.split(' ')[0]}</span>
            
            {/* Theme Toggle - HIDDEN until dark mode is properly fixed */}
            {/* <ThemeToggle /> */}
            
            {/* Settings */}
            <button 
              onClick={() => navigate('/settings')} 
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-forest-900 dark:hover:text-forest-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" 
              data-testid="settings-btn" 
              title="Settings"
            >
              <SettingsIcon className="h-5 w-5" />
            </button>
            
            {/* Admin Dashboard (only for admin) */}
            {isAdmin && (
              <button 
                onClick={() => navigate('/admin')} 
                className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors" 
                data-testid="admin-btn" 
                title="Admin Dashboard"
              >
                <Shield className="h-5 w-5" />
              </button>
            )}
            
            {/* Logout */}
            <button 
              onClick={handleLogout} 
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
              data-testid="logout-btn" 
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
            
            {/* Mobile Menu Toggle */}
            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link to="/dashboard" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <BarChart3 className="h-5 w-5 mr-3 text-forest-600 dark:text-forest-400" />
              Dashboard
            </Link>
            <Link to="/market-analysis" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <TrendingUp className="h-5 w-5 mr-3 text-forest-600 dark:text-forest-400" />
              Market Analysis
            </Link>
            <Link to="/portfolio/list" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Wallet className="h-5 w-5 mr-3 text-forest-600 dark:text-forest-400" />
              Portfolios
            </Link>
            
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 mx-3"></div>
            <p className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">V2 Tools</p>
            
            <Link to="/guidance" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Lightbulb className="h-5 w-5 mr-3 text-yellow-500" />
              Action Guidance
            </Link>
            <Link to="/backtesting" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Activity className="h-5 w-5 mr-3 text-blue-500" />
              Strategy Backtesting
            </Link>
            <Link to="/simulation" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="nav-mobile-simulation">
              <CalendarRange className="h-5 w-5 mr-3 text-amber-500" />
              Historical Simulation
            </Link>
            <Link to="/alerts" className="flex items-center justify-between px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <span className="flex items-center">
                <Bell className="h-5 w-5 mr-3 text-red-500" />
                Alerts Center
              </span>
              {unreadAlerts > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadAlerts}</span>
              )}
            </Link>
            <Link to="/activity" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Clock className="h-5 w-5 mr-3 text-indigo-500" />
              Activity History
            </Link>
            <Link to="/ai-portfolio-builder" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="nav-mobile-ai-portfolio-builder">
              <Sparkles className="h-5 w-5 mr-3 text-emerald-500" />
              AI Portfolio Builder
              <span className="ml-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">PREMIUM</span>
            </Link>
            <Link to="/instruments/evaluate" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Target className="h-5 w-5 mr-3 text-green-500" />
              Instrument Evaluator
            </Link>
            <Link to="/instruments/compare" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Scale className="h-5 w-5 mr-3 text-purple-500" />
              Compare Instruments
            </Link>
            
            <div className="border-t border-gray-200 my-2 mx-3"></div>
            
            <Link to="/education" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <BookOpen className="h-5 w-5 mr-3 text-forest-600" />
              Education
            </Link>
            <Link to="/brokers" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <Building2 className="h-5 w-5 mr-3 text-forest-600" />
              Brokers
            </Link>
            <Link to="/settings" className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              <SettingsIcon className="h-5 w-5 mr-3 text-forest-600" />
              Settings
            </Link>

            {/* Mobile footer — Contact + social row (kept out of the main desktop nav bar) */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 mx-3"></div>
            <Link
              to="/contact"
              data-testid="nav-mobile-contact"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
            >
              <MessageSquare className="h-5 w-5 mr-3 text-forest-600 dark:text-forest-400" />
              Contact & Feedback
            </Link>
            <div className="px-3 pt-2 pb-3" data-testid="nav-mobile-social-row">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Follow us</p>
              <div className="flex items-center gap-4 text-slate-500 dark:text-slate-400">
                <a href="https://x.com/PortfolioEngine" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="https://youtube.com/@PortfolioEngine" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <Youtube className="h-5 w-5" />
                </a>
                <a href="https://reddit.com/r/PortfolioEngine" target="_blank" rel="noopener noreferrer" aria-label="Reddit" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <MessageCircle className="h-5 w-5" />
                </a>
                <a href="https://portfolioengine.beehiiv.com" target="_blank" rel="noopener noreferrer" aria-label="Newsletter" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <Mail className="h-5 w-5" />
                </a>
                <a href="https://instagram.com/_portfolioengine_" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://tiktok.com/@portfolioengine.io" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                  <Music className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
